export function getDateRange(filter: string | null): { startDate: string; endDate: string } {
    const today = new Date();
    let startDate: string;
    let endDate: string = today.toISOString().split('T')[0]; // Formato YYYY-MM-DD

    switch (filter) {
        case '-1': // Todas las Fechas
            startDate = '1900-01-01'; // O alguna fecha muy antigua
            break;
        case 'last_hour':
            const lastHour = new Date(today.getTime() - 60 * 60 * 1000);
            startDate = lastHour.toISOString();
            break;
        case 'today':
            startDate = new Date(today.setHours(0, 0, 0, 0)).toISOString().split('T')[0];
            break;
        case 'yesterday':
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            startDate = yesterday.toISOString().split('T')[0];
            endDate = yesterday.toISOString().split('T')[0];
            break;
        case '3days':
            const threeDaysAgo = new Date(today);
            threeDaysAgo.setDate(today.getDate() - 3);
            startDate = threeDaysAgo.toISOString().split('T')[0];
            break;
        case '7days':
            const sevenDaysAgo = new Date(today);
            sevenDaysAgo.setDate(today.getDate() - 7);
            startDate = sevenDaysAgo.toISOString().split('T')[0];
            break;
        case '14days':
            const fourteenDaysAgo = new Date(today);
            fourteenDaysAgo.setDate(today.getDate() - 14);
            startDate = fourteenDaysAgo.toISOString().split('T')[0];
            break;
        case '30days':
            const thirtyDaysAgo = new Date(today);
            thirtyDaysAgo.setDate(today.getDate() - 30);
            startDate = thirtyDaysAgo.toISOString().split('T')[0];
            break;
        case '60days':
            const sixtyDaysAgo = new Date(today);
            sixtyDaysAgo.setDate(today.getDate() - 60);
            startDate = sixtyDaysAgo.toISOString().split('T')[0];
            break;
        case '90days':
            const ninetyDaysAgo = new Date(today);
            ninetyDaysAgo.setDate(today.getDate() - 90);
            startDate = ninetyDaysAgo.toISOString().split('T')[0];
            break;
        case 'this_month':
            startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
            endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
            break;
        case 'this_year':
            startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
            endDate = new Date(today.getFullYear(), 11, 31).toISOString().split('T')[0];
            break;
        case '1year':
            const oneYearAgo = new Date(today);
            oneYearAgo.setFullYear(today.getFullYear() - 1);
            startDate = oneYearAgo.toISOString().split('T')[0];
            break;
        case '2year':
            const twoYearsAgo = new Date(today);
            twoYearsAgo.setFullYear(today.getFullYear() - 2);
            startDate = twoYearsAgo.toISOString().split('T')[0];
            break;
        default:
            const futureDate = new Date(today);
            futureDate.setFullYear(today.getFullYear() + 20);
            endDate = futureDate.toISOString().split('T')[0];
            startDate = '1900-01-01'; // O alguna fecha muy antigua
    }

    return { startDate, endDate };
}

export function formatDateToDDMMYYYY(date: Date | string | null): string | null {
    if (!date) {
        return null;
    }

    // Manejo especial si la fecha está en formato 'YYYY-MM-DD'
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [year, month, day] = date.split('-').map(Number); // Dividir y convertir a números
        return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    }

    // Convertir texto a objeto Date si es necesario
    const parsedDate = typeof date === 'string' ? new Date(date) : date;

    // Validar si es una fecha válida
    if (!(parsedDate instanceof Date) || isNaN(parsedDate.getTime())) {
        return null;
    }

    // Usar métodos locales para respetar la zona horaria local
    const day = String(parsedDate.getDate()).padStart(2, '0');
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0'); // Los meses comienzan en 0
    const year = parsedDate.getFullYear();

    return `${day}/${month}/${year}`;
}

export function formatDateToReadableString(dateString: string | null): string {
    if (dateString === null || dateString === '') {
        return 'Sin datos';
    } else {
        const formatDate = new Date(dateString);
        const day = formatDate.getDate();
        const monthIndex = formatDate.getMonth();
        const year = formatDate.getFullYear();
        const hour = formatDate.getHours();
        const minutes = formatDate.getMinutes();
        const minutesFormatted = String(minutes).padStart(2, '0');
        const months = [
            'enero',
            'febrero',
            'marzo',
            'abril',
            'mayo',
            'junio',
            'julio',
            'agosto',
            'septiembre',
            'octubre',
            'noviembre',
            'diciembre'
        ];
        const month = months[monthIndex];

        return `${day} ${month} ${year} ${hour}:${minutesFormatted} hrs`;
    }
}

// Convierte una fecha en formato Date a una cadena en el formato dd/MM/yyyy
export function formatDate(input: Date | string, separator: string = '/'): string {
    let date: Date;

    if (typeof input === 'string') {
        // Verifica si el formato es dd-mm-yyyy
        let match = input.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (match) {
            const [, day, month, year] = match;
            date = new Date(Number(year), Number(month) - 1, Number(day));
        } else {
            // Verifica si el formato es yyyy-mm-dd
            match = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (match) {
                const [, year, month, day] = match;
                date = new Date(Number(year), Number(month) - 1, Number(day));
            } else {
                // Verifica si el formato es dd/mm/yyyy
                match = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                if (match) {
                    const [, day, month, year] = match;
                    date = new Date(Number(year), Number(month) - 1, Number(day));
                } else {
                    throw new Error('Formato de fecha inválido. Use "dd-mm-yyyy", "yyyy-mm-dd", "dd/mm/yyyy" o un objeto Date.');
                }
            }
        }
    } else if (input instanceof Date) {
        date = input;
    } else {
        throw new Error('Tipo de entrada inválido. Use "dd-mm-yyyy", "yyyy-mm-dd", "dd/mm/yyyy" o un objeto Date.');
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() es 0-indexado
    const year = date.getFullYear();

    return `${day}${separator}${month}${separator}${year}`;
}


// Convierte una cadena de fecha en formato dd/MM/yyyy a un objeto Date
export function parseDate(value: string | Date): Date | null {
    if (value instanceof Date) {
        // Si ya es una instancia de Date, validar que no sea inválida
        return isNaN(value.getTime()) ? null : value;
    }

    let day, month, year;

    if (value.includes('/')) {
        // Parse as DD/MM/YYYY
        const parts = value.split('/');
        if (parts.length !== 3) return null;
        [day, month, year] = parts.map(Number);
    } else if (value.includes('-')) {
        const parts = value.split('-');
        if (parts.length !== 3) return null;

        // Determinar si es YYYY-MM-DD o DD-MM-YYYY
        if (parts[0].length === 4) {
            // Parse as YYYY-MM-DD
            [year, month, day] = parts.map(Number);
        } else {
            // Parse as DD-MM-YYYY
            [day, month, year] = parts.map(Number);
        }
    } else {
        return null;
    }

    // Validate numbers and date range
    if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year) || day < 1 || day > 31 || month < 1 || month > 12) {
        return null;
    }

    // Create and validate the Date object
    const parsedDate = new Date(year, month - 1, day);
    if (parsedDate.getFullYear() !== year || parsedDate.getMonth() !== month - 1 || parsedDate.getDate() !== day) {
        return null;
    }

    return parsedDate;
}

export function getDatesFiltersRange(type: string): { startDate: string; endDate: string } {
    // Mapa para asociar los tipos con los rangos de días
    const rangeType: Record<string, { day1: number; day2: number }> = {
        urgent: { day1: -60, day2: 3 },
        delayed: { day1: 4, day2: 10 },
        onTime: { day1: 11, day2: 20 },
        ideal: { day1: 21, day2: 40 }
    };

    // Verificar si el tipo proporcionado es válido
    const range = rangeType[type];
    if (!range) {
        throw new Error(`Tipo inválido: ${type}. Los tipos válidos son: ${Object.keys(rangeType).join(', ')}`);
    }

    const day = new Date();

    // Calcular las fechas basadas en el rango seleccionado
    const date1 = new Date(day.getTime() + range.day1 * 24 * 60 * 60 * 1000);
    const date2 = new Date(day.getTime() + range.day2 * 24 * 60 * 60 * 1000);

    // Formatear las fechas
    const dateFormat1 = date1.toISOString().split('T')[0];
    const dateFormat2 = date2.toISOString().split('T')[0];

    return {
        startDate: dateFormat1,
        endDate: dateFormat2
    };
}
